import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const formatDate = (dateString) => {
  if (!dateString) {
    return '—';
  }
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const ICONS = {
  calendar: '📅',
  clock: '⏰',
  check: '✅',
  exclamation: '⚠️',
  archive: '📦',
};

export default function DashboardPage() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const { data } = await api.get('/dashboard/overview');
        setOverview(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, []);

  const stats = useMemo(() => {
    if (!overview) {
      return [];
    }

    const getFiscalYearRange = () => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      let startYear, endYear;
      if (currentMonth >= 4) {
        startYear = currentYear;
        endYear = currentYear + 1;
      } else {
        startYear = currentYear - 1;
        endYear = currentYear;
      }
      return {
        from: `${startYear}-04-01`,
        to: `${endYear}-03-31`
      };
    };

    const fiscalYear = getFiscalYearRange();

    return [
      {
        label: 'Total Bookings',
        value: overview.totalBookings ?? overview.bookingsCount ?? 0,
        detail: `Fiscal Year (${fiscalYear.from.split('-')[0]}-${fiscalYear.to.split('-')[0]})`,
        accent: 'blue',
        icon: 'calendar',
        onClick: () =>
          navigate(
            `/reports?dateFrom=${fiscalYear.from}&dateTo=${fiscalYear.to}&excludeCancelled=true`
          ),
      },
      {
        label: 'Material Deadline',
        value: overview.materialDeadlineCount ?? 0,
        detail: 'Due today & tomorrow',
        accent: 'red',
        icon: 'exclamation',
        onClick: () => {
          const today = new Date();
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          navigate(`/reports?materialDueFrom=${formatDate(today)}&materialDueTo=${formatDate(tomorrow)}&excludeCancelled=true`);
        },
      },
      {
        label: 'Upcoming (7 Days)',
        value: overview.upcomingCount ?? 0,
        detail: 'Upcoming releases',
        accent: 'purple',
        icon: 'calendar',
        onClick: () => {
          const today = new Date();
          const nextWeek = new Date();
          nextWeek.setDate(today.getDate() + 7);
          const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          navigate(`/reports?dateFrom=${formatDate(today)}&dateTo=${formatDate(nextWeek)}`);
        },
      },
      {
        label: 'Archived',
        value: overview.archivedCount ?? 0,
        detail: 'Released > 24h ago',
        accent: 'gray',
        icon: 'archive',
        onClick: () => navigate('/archive'),
      },
    ];
  }, [overview, navigate]);

  const upcomingReleases = useMemo(() => {
    return overview?.upcomingReleases || [];
  }, [overview?.upcomingReleases]);

  const personalReleases = useMemo(() => {
    return overview?.personalReleases || [];
  }, [overview?.personalReleases]);

  if (loading) {
    return <div className="page-content">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-stats">
        {stats.map((stat) => (
          <div 
            key={stat.label} 
            className={`stat-card ${stat.onClick ? 'clickable' : ''}`}
            onClick={stat.onClick}
            style={stat.onClick ? { cursor: 'pointer' } : {}}
          >
            <div className="stat-info">
              <span className="stat-label">{stat.label}</span>
              <span className="stat-value">{stat.value}</span>
              <span className={`stat-detail ${stat.accent === 'blue' ? 'blue' : 'muted'}`}>
                {stat.detail}
              </span>
            </div>
            <div className={`stat-icon-box ${stat.accent}`}>
              {ICONS[stat.icon] || ICONS.calendar}
            </div>
          </div>
        ))}
      </div>

      <section className="section-card">
        <div className="section-header">
          <h3>Upcoming Releases</h3>
          <span className="section-sub">Next 7 days across all bookings</span>
        </div>
        <div className="upcoming-list">
          {upcomingReleases.map((release) => (
            <div
              key={`${release.booking_id}-${release.release_date}`}
              className={`upcoming-item ${release.booking_source?.toLowerCase() === 'publication' ? 'publication' : ''}`}
            >
              <div className="upcoming-main">
                <div className="upcoming-tags">
                  <span className={`tag-pill ${release.status_name?.toLowerCase() === 'confirmed' ? 'confirmed' : 'pending'}`}>
                    {release.status_name || 'Pending'}
                  </span>
                </div>
                <div className="upcoming-title">
                  <strong>{release.client || 'N/A'}</strong>
                </div>
                <div className="upcoming-meta">
                  <span>{release.publication || release.platform || 'Unknown Publication'} — {release.position || release.edition || 'General'}</span>
                  <span>Release: {formatDate(release.release_date)}</span>
                  {release.material_due_date && <span>Material due: {formatDate(release.material_due_date)}</span>}
                </div>
              </div>
              <div className="upcoming-actions">
                <span className="action-link" onClick={() => navigate(`/bookings/view/${release.booking_id}`)}>View</span>
                <span className="action-link" onClick={() => navigate(`/bookings/edit/${release.booking_id}`)}>Edit</span>
              </div>
            </div>
          ))}
          {!upcomingReleases.length && <p className="muted">No releases scheduled for the next week.</p>}
        </div>
      </section>

      <section className="section-card">
        <div className="section-header">
          <h3>My Upcoming Releases & Tasks</h3>
          <span className="section-sub">All your assigned bookings and deadlines</span>
        </div>
        <div className="upcoming-list">
          {personalReleases.map((release) => (
            <div
              key={`personal-${release.booking_id}-${release.release_date}`}
              className={`upcoming-item ${release.booking_source?.toLowerCase() === 'publication' ? 'publication' : ''}`}
            >
              <div className="upcoming-main">
                <div className="upcoming-tags">
                  <span className={`tag-pill ${release.status_name?.toLowerCase() === 'confirmed' || release.status_name?.toLowerCase() === 'completed' ? 'confirmed' : 'pending'}`}>
                    {release.status_name || 'Pending'}
                  </span>
                </div>
                <div className="upcoming-title">
                  <strong>{release.client || 'N/A'}</strong>
                </div>
                <div className="upcoming-meta">
                  <span>{release.publication || release.platform || 'Unknown Publication'} — {release.position || release.edition || 'General'}</span>
                  <span>Release: {formatDate(release.release_date)}</span>
                  {release.material_due_date && <span>Material due: {formatDate(release.material_due_date)}</span>}
                </div>
              </div>
              <div className="upcoming-actions">
                <span className="action-link" onClick={() => navigate(`/bookings/view/${release.booking_id}`)}>View</span>
                <span className="action-link" onClick={() => navigate(`/bookings/edit/${release.booking_id}`)}>Edit</span>
              </div>
            </div>
          ))}
          {!personalReleases.length && <p className="muted">No releases assigned to you.</p>}
        </div>
      </section>
    </div>
  );
}